import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';
import { useDispatch } from 'react-redux';

import { Modal } from '@simplywallst/ui-core';
import { Button } from '@simplywallst/ui-core/next';
import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';
import { cacheResults } from '@/components/Search/redux/routines';

import type { Watchlist } from '@/hooks/useWatchlist';
import { useWatchlistMutations } from '@/pages/Watchlist/hooks/useWatchlistMutations';

const AlgoliaSearch = loadable(
  () => import('@/components/Search/components/AlgoliaSearch')
);

const LimitModal = loadable(() => import('../../components/LimitModal'));

type Props = {
  watchlist: Watchlist;
};

export const FloatingAddToWatchlist = ({ watchlist }: Props) => {
  const [query, setQuery] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);

  const dispatch = useDispatch();
  const { addCompany, addCompanyStatus } = useWatchlistMutations();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'footer',
  });

  const { id: watchlistId, items, itemsLimit } = watchlist;

  const handleClick = useCallback(() => {
    if (items.length === itemsLimit) {
      setShowLimitModal(true);
      return;
    }
    setShowSearchModal(!showSearchModal);
    trackEvent({
      type: 'track',
      action: 'click',
    });
  }, [items, itemsLimit, trackEvent, showSearchModal]);

  const handleClose = useCallback(() => {
    setShowSearchModal(false);
  }, []);

  const handleSelect = useCallback(
    (id: string) => {
      addCompany({ id, includeSnackBar: true, watchlistId });
      setQuery('');
      setShowSearchModal(false);
    },
    [addCompany, watchlistId]
  );

  const getResult = useCallback(
    (
      child: ReactNode,
      result: {
        uniqueSymbol: string;
      }
    ) => (
      <div
        className="cursor-pointer"
        role="button"
        onClick={() => {
          handleSelect(result.uniqueSymbol);
        }}
      >
        {child}
      </div>
    ),
    [handleSelect]
  );

  return (
    <Track>
      <div
        aria-hidden={showSearchModal ? 'true' : 'false'}
        className="fixed bottom-0 right-[max(calc(50vw-600px-72px),theme(space.x2))] z-20 mb-[env(safe-area-inset-bottom)] size-x7 -translate-y-x10 rounded-full aria-hidden:translate-y-x7"
      >
        <Button
          className="size-x7 rounded-full"
          onClick={handleClick}
          isDisabled={addCompanyStatus === 'pending'}
        >
          <PlusSolid aria-label="Add to watchlist" />
        </Button>
      </div>
      {showSearchModal && (
        <Modal isNaked onSecondaryClick={handleClose}>
          <AlgoliaSearch
            onClose={handleClose}
            onSearchResult={(results, query) => {
              setQuery(query);
              dispatch(cacheResults.trigger(results));
            }}
            onReset={() => setQuery('')}
            query={query}
            primaryWrapper={getResult}
            secondaryWrapper={getResult}
          />
        </Modal>
      )}
      {showLimitModal && (
        <LimitModal
          watchlist={watchlist}
          onClose={() => setShowLimitModal(false)}
        />
      )}
    </Track>
  );
};
